import { useCallback, useEffect, useMemo, useState, useRef, memo } from 'react'
import { Modal, View, Text, TextInput, Switch, ActionIcon } from '@/components'
import { createStyles } from '@codeleap/styles'
import { PropsOf } from '@codeleap/types'
import {
  useCanCreatePoll,
  useMessageComposer,
  useStateStore,
  useTranslationContext,
} from 'stream-chat-react'
import { Chat, textVariant } from '@/config'
import { alert } from '@codeleap/modals'
import { PollComposerState } from 'stream-chat'
import { getDuplicateIndices } from '@packages/chat'
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const MAX_OPTIONS = Chat?.Constants?.poll?.maxOptions ?? 12
const MAX_QUESTION_LENGTH = 200
const MIN_OPTIONS = 2

type PollStateData = {
  allowComments: boolean
  enforceUniqueVote: boolean
  errors: Record<string, any>
  name: string
  options: Array<{ id: string; text: string }>
}

const pollStateSelector = (state: PollComposerState): PollStateData => ({
  allowComments: state.data.allow_answers,
  enforceUniqueVote: state.data.enforce_unique_vote,
  errors: state.errors || {},
  name: state.data.name || '',
  options: state.data.options || [],
})

type CreatePollModalProps = PropsOf<typeof Modal>

// Memoized option input to prevent re-renders when other options change
const OptionInput = memo(({
  option,
  index,
  hasDuplicateError,
  optionError,
  onValueChange,
  onBlur,
  dragHandleProps,
  t,
}: {
  option: { id: string; text: string }
  index: number
  hasDuplicateError: boolean
  optionError?: string
  onValueChange: (index: number, text: string) => void
  onBlur: () => void
  dragHandleProps?: { listeners: any; attributes: any }
  t: any
}) => {
  const [localText, setLocalText] = useState(option.text)
  const inputRef = useRef<HTMLInputElement>(null)
  const selectionRef = useRef<{ start: number; end: number } | null>(null)

  useEffect(() => {
    if (option.text !== localText) {
      setLocalText(option.text)
    }
  }, [option.text])

  const handleChange = useCallback((text: string) => {
    const input = inputRef.current
    if (input) {
      selectionRef.current = {
        start: input.selectionStart ?? text.length,
        end: input.selectionEnd ?? text.length,
      }
    }

    setLocalText(text)
    onValueChange(index, text)

    requestAnimationFrame(() => {
      if (inputRef.current && selectionRef.current) {
        const { start, end } = selectionRef.current
        inputRef.current.setSelectionRange(start, end)
      }
    })
  }, [onValueChange, index])

  const inputStyle = useMemo(() => [
    'fullWidth',
    'noError',
    hasDuplicateError ? styles.inputWithError : styles.inputDefault,
  ], [hasDuplicateError])

  const rightIconProps = useMemo(() => {
    const base = { name: 'menu-05' as const }
    if (dragHandleProps) {
      return {
        ...base,
        ...dragHandleProps.listeners,
        ...dragHandleProps.attributes,
        style: styles.dragIconStyle,
      }
    }
    return base
  }, [dragHandleProps])

  return (
    <View style={styles.optionRow}>
      <TextInput
        ref={inputRef}
        value={localText}
        onValueChange={handleChange}
        onBlur={onBlur}
        placeholder={t ? t('Add option') : 'Add option'}
        style={inputStyle}
        debugName={`CreatePoll:OptionInput:${index}`}
        maxLength={100}
        rightIcon={rightIconProps}
      />
      {hasDuplicateError ? (
        <Text text={t ? t('This is already an option') : 'This is already an option'} style={styles.errorText} />
      ) : optionError ? (
        <Text text={optionError} style={styles.errorText} />
      ) : null}
    </View>
  )
})

const SortableOption = memo(({
  option,
  index,
  hasDuplicateError,
  optionError,
  onValueChange,
  onBlur,
  t,
}: {
  option: { id: string; text: string }
  index: number
  hasDuplicateError: boolean
  optionError?: string
  onValueChange: (index: number, text: string) => void
  onBlur: () => void
  t: any
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: option.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <OptionInput
        option={option}
        index={index}
        hasDuplicateError={hasDuplicateError}
        optionError={optionError}
        onValueChange={onValueChange}
        onBlur={onBlur}
        dragHandleProps={{ listeners, attributes }}
        t={t}
      />
    </div>
  )
})

const CreatePollModalContent = memo(({ visible, toggle, ...modalProps }: CreatePollModalProps) => {
  const { t } = useTranslationContext()
  const messageComposer = useMessageComposer()
  const { pollComposer, createPoll, clear } = messageComposer
  const canCreatePoll = useCanCreatePoll()

  const { enforceUniqueVote, errors, name, options } = useStateStore(
    pollComposer.state,
    pollStateSelector,
  )

  const [submitting, setSubmitting] = useState(false)
  const [localName, setLocalName] = useState('')
  const mountedRef = useRef(true)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (visible) {
      pollComposer.initState()
      setLocalName('')
    }
  }, [visible])

  useEffect(() => {
    if (visible && name !== localName) {
      setLocalName(name)
    }
  }, [visible, name])

  useEffect(() => {
    if (!visible) return
    if (options.length >= MIN_OPTIONS) return

    const current = pollComposer.options || []
    const required = Math.max(0, MIN_OPTIONS - current.length)

    if (required > 0) {
      pollComposer.updateFields({
        options: [
          ...current,
          ...Array.from({ length: required }, () => ({
            id: generateUUID(),
            text: '',
          })),
        ],
      })
    }
  }, [visible, options.length])

  const handleClose = useCallback(() => {
    pollComposer.initState()
    toggle?.()
  }, [pollComposer, toggle])

  const selectionRef = useRef<{ start: number; end: number }>({
  start: 0,
  end: 0,
})


 const handleQuestionChange = useCallback(
  (value: string) => {
    const input = inputRef.current
    if (input) {
      const start = input.selectionStart ?? value.length
      const end = input.selectionEnd ?? value.length

      selectionRef.current = { start, end }
    }

    setLocalName(value)
    pollComposer.updateFields({ name: value })

    requestAnimationFrame(() => {
      if (inputRef.current && selectionRef.current) {
        const { start, end } = selectionRef.current
        inputRef.current.setSelectionRange(start, end)
      }
    })
  },
  [pollComposer],
)
  const handleQuestionBlur = useCallback(() => {
    pollComposer.handleFieldBlur('name')
  }, [pollComposer])

  const handleOptionChange = useCallback(
    (index: number, text: string) => {
      pollComposer.updateFields({
        options: { index, text },
      })
    },
    [pollComposer],
  )

  const handleOptionBlur = useCallback(() => {
    pollComposer.handleFieldBlur('options')
  }, [pollComposer])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = options.findIndex((option) => option.id === active.id)
    const newIndex = options.findIndex((option) => option.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    const newOptions = arrayMove(options, oldIndex, newIndex)

    pollComposer.updateFields({
      options: newOptions,
    })
  }, [options, pollComposer])

  const handleMultipleAnswersToggle = useCallback(
    async (value: boolean) => {
      await pollComposer.updateFields({ enforce_unique_vote: !value })
    },
    [pollComposer],
  )

  const multipleAnswersEnabled = useMemo(() => !enforceUniqueVote, [enforceUniqueVote])

  const questionError = (errors as any)?.name
  const optionErrors = ((errors as any)?.options || {}) as Record<string, string>
  const visibleOptions = useMemo(() => options.slice(0, MAX_OPTIONS), [options])
  const duplicateIndices = useMemo(() => getDuplicateIndices(options), [options])

  const handleCreatePoll = useCallback(async () => {
    if (submitting || !canCreatePoll) return

    setSubmitting(true)
    try {
      const quotedMessageId = messageComposer.state.getLatestValue?.()?.quotedMessage?.id
      await createPoll()
      await Chat.API.sendPoll(
        pollComposer.composer.channel,
        pollComposer,
        quotedMessageId ? { quotedMessageId } : undefined,
      )
      clear()

      if (mountedRef.current) {
        pollComposer.initState()
        toggle?.()
      }
    } catch (error) {
      console.error('Failed to create poll', error)

      if (mountedRef.current) {
        alert.error({
          title: t?.('Error') || 'Error',
          body: t?.('Failed to create poll. Please try again.') || 'Failed to create poll. Please try again.',
        })
      }
    } finally {
      if (mountedRef.current) {
        setSubmitting(false)
      }
    }
  }, [canCreatePoll, createPoll, clear, toggle, pollComposer, messageComposer])

  return (
    <Modal
      title={t ? t('Create poll') : 'Create poll'}
      showClose
      style={styles.modalComposed}
      visible={visible}
      toggle={handleClose}
      {...modalProps}
    >
      <View style={styles.wrapper}>
        <View style={styles.section}>
          <Text text={t ? t('Question') : 'Question'} style={styles.sectionLabel} />
          <TextInput
            value={localName}
            onValueChange={handleQuestionChange}
            onBlur={handleQuestionBlur}
            placeholder={t ? t('Ask a question') : 'Ask question'}
            style={styles.questionInputComposed}
            debugName='CreatePoll:QuestionInput'
            autoFocus
            maxLength={MAX_QUESTION_LENGTH}
            ref={inputRef}
          />
          {questionError ? <Text text={questionError} style={styles.errorText} /> : null}
        </View>

        <View style={styles.contentSection}>
          <Text text={t ? t('Options') : 'Options'} style={styles.sectionLabel} />
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={visibleOptions.map((o) => o.id)}
              strategy={verticalListSortingStrategy}
            >
              <View style={styles.optionsWrapper}>
                {visibleOptions.map((option: any, index: number) => (
                  <SortableOption
                    key={option.id || index}
                    option={option}
                    index={index}
                    hasDuplicateError={duplicateIndices.has(index)}
                    optionError={optionErrors?.[option.id]}
                    onValueChange={handleOptionChange}
                    onBlur={handleOptionBlur}
                    t={t}
                  />
                ))}
              </View>
            </SortableContext>
          </DndContext>
        </View>

        <View style={styles.toggleRow}>
          <Text text={t ? t('Multiple answers') : 'Multiple answers'} style={styles.toggleLabel} />
          <Switch
            value={multipleAnswersEnabled}
            onValueChange={handleMultipleAnswersToggle}
            debugName='CreatePoll:MultipleAnswers'
          />
        </View>
        <View style={styles.footer}>
          <ActionIcon
            icon='arrow-up'
            style={styles.submitButtonComposed}
            onPress={handleCreatePoll}
            disabled={!canCreatePoll || submitting}
            debugName='CreatePoll:Submit'
          />
        </View>
      </View>
    </Modal>
  )
})

// Export wrapper to handle the case when pollComposer might not be available
export const CreatePollModal = (props: CreatePollModalProps) => {
  return <CreatePollModalContent {...props} />
}

const styles = createStyles(theme => ({
  // Pre-composed styles to avoid inline object creation
  modalComposed: [
    'center',
    'box:noPadding',
    {
      box: {
        maxWidth: 420,
        width: '100%',
        [theme.media.down('tabletSmall')]: {
          maxWidth: `calc(100% - ${theme.spacing.value(4)}px)`,
        },
      },
      titleWrapper: {
        ...theme.spacing.padding(2),
        ...theme.spacing.paddingBottom(1),
      },
    },
  ] as any,
  questionInputComposed: [
    'fullWidth',
    'noError',
    { input: { maxHeight: 42 }},
  ] as any,
  inputDefault: {
    input: { maxHeight: 42 },
  },
  inputWithError: {
    input: { maxHeight: 42 },
    innerWrapper: {
      ...theme.border({
        color: theme.colors.fieldNegativeStrokeDefault,
        width: theme.stroke.small,
      }),
    },
    'innerWrapper:focus': {
      ...theme.border({
        color: theme.colors.fieldNegativeStrokeDefault,
        width: theme.stroke.small,
      }),
    },
  },
  submitButtonComposed: [
    'iconSize:2',
    {
      touchableWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 52,
        height: 52,
        borderRadius: theme.radius.large,
        backgroundColor: theme.baseColors.primarySolid500,
      },
      icon: {
        color: theme.baseColors.neutralSolid0,
      },
    },
  ] as any,
  wrapper: {
    ...theme.presets.column,
    ...theme.spacing.gap(2),
    ...theme.spacing.paddingHorizontal(2),
    ...theme.spacing.paddingBottom(3),
    maxHeight: '80svh',
    overflowY: 'auto',
  },
  section: {
    ...theme.presets.column,
    ...theme.spacing.gap(1),
  },
  contentSection: {
    ...theme.presets.column,
    ...theme.spacing.gap(1),
    ...theme.spacing.marginBottom(2),
  },
  sectionLabel: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingRegular,
  },
  errorText: {
    ...textVariant('c3'),
    color: theme.colors.fieldNegativeLabelDefault,
  },
  optionsWrapper: {
    ...theme.presets.column,
    ...theme.spacing.gap(1),
  },
  optionRow: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.5),
  },
  addOptionButton: {
    alignSelf: 'flex-start',
  },
  toggleRow: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.presets.justifySpaceBetween,
    ...theme.spacing.padding(2),
    ...theme.spacing.marginBottom(4),
    backgroundColor: theme.baseColors.neutralBlackTransparent50,
    borderRadius: theme.radius.regular,
  },
  toggleLabel: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingRegular,
  },
  footer: {
    ...theme.presets.row,
    ...theme.presets.justifyEnd,
  },
  dragIconStyle: {
    cursor: 'grab',
    icon: {
      color: theme.baseColors.neutralSolid500,
      width: 18,
      height: 18,
      transition: 'color 0.2s ease, stroke 0.2s ease, fill 0.2s ease',
    },
    touchableWrapper: {
      backgroundColor: 'transparent',
      border: 'none',
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: 'transparent',
        border: 'none',
      },
      '&:hover svg': {
        color: `${theme.baseColors.primarySolid500} !important`,
      },
      '&:hover svg path, &:hover path': {
        stroke: `${theme.baseColors.primarySolid500} !important`,
        fill: `${theme.baseColors.primarySolid500} !important`,
      },
      '&:hover *': {
        color: `${theme.baseColors.primarySolid500} !important`,
      },
      '&:active': {
        backgroundColor: 'transparent',
        border: 'none',
      },
    },
  },
}))

